import React from 'react';
import { X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { RadioRow } from './_shared';

export default function PersonalInfoStep({ formData, updateFormData }) {
  const dependents = formData.dependents || [];
  const set = (patch) => updateFormData({ ...formData, ...patch });

  const addDependent = () => set({ dependents: [...dependents, { firstName: '', lastName: '', relationship: '', birthdate: '' }] });
  const removeDependent = (idx) => set({ dependents: dependents.filter((_, i) => i !== idx) });
  const updateDependent = (idx, field, value) => {
    const next = dependents.map((d, i) => (i === idx ? { ...d, [field]: value } : d));
    set({ dependents: next });
  };

  return (
    <div data-testid="personal-info-step">
      <h2 className="text-2xl font-bold text-[#0f172a] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Personal Information
      </h2>

      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-[#0f172a]">Children and Other Dependents</h3>
          <Button onClick={addDependent} size="sm" variant="outline" data-testid="add-dependent-button">
            <span className="text-lg mr-1">+</span> Add
          </Button>
        </div>

        {dependents.map((dep, idx) => (
          <div key={idx} className="p-4 bg-[#f8fafc] rounded-md border border-[#e2e8f0] mb-4">
            <div className="flex justify-between items-start mb-4">
              <span className="text-sm font-medium text-[#475569]">Dependent #{idx + 1}</span>
              <Button onClick={() => removeDependent(idx)} size="sm" variant="ghost" data-testid={`remove-dependent-${idx}`}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>First Name</Label>
                <Input placeholder="First Name" value={dep.firstName || ''} onChange={(e) => updateDependent(idx, 'firstName', e.target.value)} className="mt-2" />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input placeholder="Last Name" value={dep.lastName || ''} onChange={(e) => updateDependent(idx, 'lastName', e.target.value)} className="mt-2" />
              </div>
              <div className="md:col-span-2">
                <Label>Relationship</Label>
                <RadioRow
                  name={`dep-rel-${idx}`}
                  value={dep.relationship}
                  onChange={(v) => updateDependent(idx, 'relationship', v)}
                  options={[
                    { label: 'Child', value: 'child' },
                    { label: 'Grandchild', value: 'grandchild' },
                    { label: 'Other Dependent', value: 'other' },
                  ]}
                />
              </div>
              <div>
                <Label>Birthdate</Label>
                <Input type="date" value={dep.birthdate || ''} onChange={(e) => updateDependent(idx, 'birthdate', e.target.value)} className="mt-2" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div>
        <h3 className="text-lg font-medium text-[#0f172a] mb-4">Estate Documents</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label>Do you have a Will?</Label>
            <RadioRow name="hasWill" value={formData.hasWill} onChange={(v) => set({ hasWill: v })} options={[{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }]} testIdPrefix="haswill" />
          </div>
          <div>
            <Label>Includes Bypass Trust?</Label>
            <RadioRow name="bypassTrust" value={formData.bypassTrust} onChange={(v) => set({ bypassTrust: v })} options={[{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }]} />
          </div>
          <div>
            <Label htmlFor="dateLastReviewed">Date Last Reviewed</Label>
            <Input id="dateLastReviewed" type="date" value={formData.dateLastReviewed || ''} onChange={(e) => set({ dateLastReviewed: e.target.value })} className="mt-2" />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="estateNotes">Notes</Label>
            <Textarea id="estateNotes" value={formData.estateNotes || ''} onChange={(e) => set({ estateNotes: e.target.value })} className="mt-2" rows={3} placeholder="Any additional notes about your estate documents..." />
          </div>
        </div>
      </div>
    </div>
  );
}
